import React, { useState, useEffect, useRef } from 'react';
import { createRoot } from 'react-dom/client';
import { Route, HashRouter, withRouter, RouteComponentProps } from 'react-router-dom';
import FleetPage from './FleetPage';
import BoxPage from './BoxPage';
import CustomerSelect, { CustomerItem } from './CustomerSelect';
import AuthForm from './AuthForm';
import { setRootComponent } from '../../faseMonitor/actions';
import { getFaseCustomers } from '../../faseMonitor/service';
import {
  clearCredentials,
  loginWithFaseCredentials,
  logoutFaseSession,
  setOnUnauthorized,
  refreshFaseSessionStatus,
  isUnauthorizedError,
} from '../../faseMonitor/auth-service';
import '../../css/fase-monitor.css';

interface FaseAppState {
  filterHealth: string | null;
  logoUrl: string | null;
  customerName: string | null;
  logoLoadFailed: boolean;
  activeCustomerCode: string | null;
  customers: CustomerItem[];
  authChecking: boolean;
  authRequired: boolean;
  authError: string | null;
  showFaseLogout: boolean;
}

const initialFaseAppState: FaseAppState = {
  filterHealth: null,
  logoUrl: null,
  customerName: null,
  logoLoadFailed: false,
  activeCustomerCode: null,
  customers: [],
  authChecking: true,
  authRequired: false,
  authError: null,
  showFaseLogout: false,
};

type FaseMonitorAppProps = RouteComponentProps;

function FaseMonitorApp({ history, location }: FaseMonitorAppProps) {
  const [state, setState] = useState(initialFaseAppState);

  const themeGenRef = useRef(0);
  // Keep latest state for the module-level root adapter registered once below.
  // Without this, onHealthRowClick closes over the first render's state and
  // cannot toggle an active health filter back off.
  const stateRef = useRef(state);
  stateRef.current = state;

  useEffect(() => {
    setRootComponent({
      get state() {
        return stateRef.current;
      },
      setState: (updater: any) => {
        setState((prevState) => {
          const patch = typeof updater === 'function' ? updater(prevState) : updater;
          return { ...prevState, ...patch };
        });
      },
      get _themeGeneration() {
        return themeGenRef.current;
      },
      set _themeGeneration(val: number) {
        themeGenRef.current = val;
      },
    });
    setOnUnauthorized(() => {
      setState((prev) => ({ ...prev, authRequired: true, showFaseLogout: false }));
    });

    async function initAuth() {
      try {
        const customers = await getFaseCustomers();
        const showFaseLogout = await refreshFaseSessionStatus();
        setState((prev) => ({
          ...prev,
          customers,
          authChecking: false,
          authRequired: false,
          showFaseLogout,
        }));
      } catch (error) {
        if (!isUnauthorizedError(error)) {
          console.error('Failed to load FASE customers', error);
        }
        setState((prev) => ({ ...prev, authChecking: false, authRequired: true, showFaseLogout: false }));
      }
    }

    initAuth();
  }, []);

  const handleAuthSubmit = async (username: string, password: string) => {
    try {
      const customers = await loginWithFaseCredentials(username, password);
      setState((prev) => ({ ...prev, customers, authRequired: false, authError: null, showFaseLogout: true }));
    } catch (error) {
      clearCredentials();
      if (isUnauthorizedError(error)) {
        setState((prev) => ({ ...prev, authError: 'Invalid username or password. Please try again.' }));
      } else {
        setState((prev) => ({ ...prev, authError: 'Unable to sign in. Please check your connection and try again.' }));
      }
    }
  };

  const handleLogout = async () => {
    await logoutFaseSession().catch(() => {});
    history.replace('/');
    setState((prev) => ({
      ...prev,
      authChecking: true,
      authRequired: false,
      authError: null,
      customers: [],
      showFaseLogout: false,
    }));

    try {
      const customers = await getFaseCustomers();
      setState((prev) => ({ ...prev, customers, authChecking: false, authRequired: false }));
    } catch (error) {
      setState((prev) => ({ ...prev, authChecking: false, authRequired: true }));
    }
  };

  const onCustomerSelect = (customerCode: string) => {
    history.push(`/${customerCode}`);
  };

  const getSelectedCustomerCode = () => {
    const segment = location.pathname.split('/').filter(Boolean)[0];
    if (segment && segment !== 'box') {
      return segment.toUpperCase();
    }
    return state.activeCustomerCode;
  };

  const isCustomerAllowed = (customerCode: string) => {
    if (!customerCode) {
      return true;
    }
    const { customers } = state;
    if (!customers.length) {
      return true;
    }
    const normalizedCode = customerCode.toUpperCase();
    return customers.some((customer) => customer.customerCode.toUpperCase() === normalizedCode);
  };

  const renderFleetPage = (customerCode?: string) => {
    const normalizedCode = customerCode ? customerCode.toUpperCase() : null;
    if (normalizedCode && state.customers.length > 0 && !isCustomerAllowed(normalizedCode)) {
      return (
        <div className="fase-monitor__status fase-monitor__status--error">
          You do not have access to airline {normalizedCode}.
        </div>
      );
    }

    return <FleetPage key={customerCode || 'default'} customerCode={customerCode} filterHealth={state.filterHealth} />;
  };

  const onLogoError = () => {
    setState((prev) => ({ ...prev, logoLoadFailed: true }));
  };

  const renderHeaderBranding = () => {
    const { logoUrl, customerName, logoLoadFailed, activeCustomerCode } = state;
    const customerCode = activeCustomerCode || getSelectedCustomerCode();

    if (logoUrl && !logoLoadFailed) {
      return (
        <div className="fase-monitor__branding">
          <img src={logoUrl} alt="" onError={onLogoError} />
        </div>
      );
    }

    const brandingLabel = customerName || customerCode;
    if (brandingLabel) {
      return <div className="fase-monitor__branding fase-monitor__branding-name">{brandingLabel}</div>;
    }

    return <div className="fase-monitor__branding" />;
  };

  if (state.authChecking) {
    return (
      <div className="fase-monitor">
        <div className="fase-monitor__status">Loading...</div>
      </div>
    );
  }

  if (state.authRequired) {
    return <AuthForm onSubmit={handleAuthSubmit} error={state.authError} />;
  }

  const selectedCustomerCode = getSelectedCustomerCode();
  const isBoxPage = location.pathname.startsWith('/box/');

  return (
    <div className="fase-monitor">
      <header className="fase-monitor__header">
        <div className="fase-monitor__header-start">{renderHeaderBranding()}</div>
        <div className="fase-monitor__header-center">
          {!isBoxPage && (
            <>
              <h1 className="fase-monitor__header-title-row">
                <span>FASE Fleet Monitor</span>
              </h1>
              <p className="fase-monitor__header-subtitle">Live health overview for aircraft and onboard boxes</p>
            </>
          )}
        </div>
        <div className="fase-monitor__header-end">
          <CustomerSelect
            customers={state.customers}
            value={selectedCustomerCode || undefined}
            onChange={onCustomerSelect}
          />
          {state.showFaseLogout && (
            <button type="button" className="fase-monitor__logout" onClick={handleLogout}>
              Log out
            </button>
          )}
        </div>
      </header>
      <Route exact path="/" render={() => renderFleetPage()} />
      <Route
        exact
        path="/:customerCode"
        render={(props) => {
          if (props.match.params.customerCode === 'box') {
            return null;
          }
          return renderFleetPage(props.match.params.customerCode);
        }}
      />
      <Route exact path="/box/:serial" render={(props) => <BoxPage serial={props.match.params.serial} />} />
    </div>
  );
}

const FaseMonitorAppWithRouter = withRouter(FaseMonitorApp);

const rootEl = document.getElementById('app-root');
if (rootEl) {
  createRoot(rootEl).render(
    <HashRouter>
      <FaseMonitorAppWithRouter />
    </HashRouter>,
  );
}
